import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ENDPOINTS } from './../../endpoints';
import { Service } from './../base';
import dynamicAxios from './../../utils/axios';
import useServices from '../../hooks/useServices';

import { PendingSurveysResponse, SubmitSurveyResponseBody, SubmitSurveyResponseResponse, SurveyKey } from './types';

/**
 * In-house product surveys (core-services `RestWorld.Surveys`): responses live
 * in our database, so surveys reach every logged-in user regardless of cookie
 * consent. This service is the whole data layer — each survey renders its own
 * UI (a dialog, a wizard) and only talks to these two endpoints.
 */
class ServiceSurveys extends Service {
  getPendingSurveys() {
    return this.axiosInstance.get<PendingSurveysResponse>(ENDPOINTS.surveys.pending);
  }

  submitSurveyResponse(surveyKey: SurveyKey, body: SubmitSurveyResponseBody) {
    return this.axiosInstance.post<SubmitSurveyResponseResponse>(ENDPOINTS.surveys.response(surveyKey), body);
  }
}

export function useSurveysService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceSurveys(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

const PENDING_SURVEYS_QUERY_KEY = ['surveys', 'pending'];

/**
 * The "pipe" a survey UI plugs into: server-side eligibility plus submit and
 * dismiss mutations for one survey key.
 *
 * `isPending` is `undefined` while eligibility is loading, then a boolean —
 * callers that gate rendering on it should treat `undefined` as "not yet".
 * Repeatable surveys (backend dedup `:none`, e.g. the open feedback) are
 * always pending by design.
 */
export function useSurvey(surveyKey: SurveyKey) {
  const service = useSurveysService();
  const queryClient = useQueryClient();

  const pendingQuery = useQuery({
    queryKey: PENDING_SURVEYS_QUERY_KEY,
    queryFn: async () => {
      const response = await service.getPendingSurveys();
      return response.data.data.pending;
    },
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: (body: SubmitSurveyResponseBody) => service.submitSurveyResponse(surveyKey, body),
    onSuccess: () => {
      // A recorded response can change eligibility (user/organization-deduped
      // surveys leave the pending list).
      queryClient.invalidateQueries({ queryKey: PENDING_SURVEYS_QUERY_KEY });
    },
  });

  return {
    isPending: pendingQuery.data === undefined ? undefined : pendingQuery.data.includes(surveyKey),
    isEligibilityLoading: pendingQuery.isLoading,
    submit: (answers: Record<string, unknown>) => mutation.mutateAsync({ status: 'answered', answers }),
    dismiss: () => mutation.mutateAsync({ status: 'dismissed' }),
    isSubmitting: mutation.isPending,
  };
}

export default useSurveysService;

export * from './types';
